import React from 'react';
import { Button, FormControl, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { CollectionEmailTemplate, TONE_LABELS } from '../../config/model';
import {
  EMPTY_SCHEDULED_EMAIL_FILTERS,
  SCHEDULE_STATUS_FILTER_OPTIONS,
  ScheduledEmailFilterFields,
} from '../filterScheduledEmails';

interface Props {
  filters: ScheduledEmailFilterFields;
  onChange: (filters: ScheduledEmailFilterFields) => void;
  onClear: () => void;
}

const TONES: CollectionEmailTemplate['tone'][] = ['friendly', 'second_notice', 'urgent'];

const ScheduledEmailFilters: React.FC<Props> = ({ filters, onChange, onClear }) => {
  const handleChange = <K extends keyof ScheduledEmailFilterFields>(key: K, value: ScheduledEmailFilterFields[K]) => {
    onChange({ ...filters, [key]: value });
  };

  const hasActiveFilters =
    filters.company.trim() !== '' ||
    filters.tone !== '' ||
    filters.nextSendDate.trim() !== '' ||
    filters.status !== '';

  return (
    <Grid container spacing={2} alignItems="flex-end">
      <Grid item xs={12} sm={6} md={3}>
        <TextField
          label="Company"
          value={filters.company}
          onChange={(e) => handleChange('company', e.target.value)}
          size="small"
          fullWidth
          variant="outlined"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <FormControl size="small" fullWidth>
          <InputLabel id="scheduled-email-filter-tone">Tone</InputLabel>
          <Select
            labelId="scheduled-email-filter-tone"
            label="Tone"
            value={filters.tone}
            onChange={(e) => handleChange('tone', e.target.value as ScheduledEmailFilterFields['tone'])}
          >
            <MenuItem value="">All</MenuItem>
            {TONES.map((tone) => (
              <MenuItem key={tone} value={tone}>
                {TONE_LABELS[tone]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <TextField
          label="Next Send"
          value={filters.nextSendDate}
          onChange={(e) => handleChange('nextSendDate', e.target.value)}
          size="small"
          fullWidth
          variant="outlined"
          placeholder="mm/dd/yyyy"
          inputProps={{ inputMode: 'numeric', 'aria-label': 'Next send date filter mm/dd/yyyy' }}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <FormControl size="small" fullWidth>
          <InputLabel id="scheduled-email-filter-status">Status</InputLabel>
          <Select
            labelId="scheduled-email-filter-status"
            label="Status"
            value={filters.status}
            onChange={(e) => handleChange('status', e.target.value as ScheduledEmailFilterFields['status'])}
          >
            <MenuItem value="">All</MenuItem>
            {SCHEDULE_STATUS_FILTER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md="auto">
        <Button variant="outlined" size="small" onClick={onClear} disabled={!hasActiveFilters} sx={{ mb: 0.25 }}>
          Clear filters
        </Button>
      </Grid>
    </Grid>
  );
};

export { EMPTY_SCHEDULED_EMAIL_FILTERS };
export default ScheduledEmailFilters;
